import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { 
  ArrowLeft, 
  Users, 
  Plus, 
  Search, 
  Trash2, 
  Edit2,
  FolderPlus,
  UserPlus,
  Mail,
  Phone,
  MoreVertical,
  Download,
  Upload,
  Loader2,
  Settings,
  CheckCircle2,
  AlertCircle,
  GripVertical,
  X,
  Calendar,
  Hash,
  Link as LinkIcon,
  MapPin,
  Globe,
  ToggleLeft,
  List,
  Type,
  Send,
  FileSpreadsheet,
  FileDown,
  Shield
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import AnimatedLogo from "../components/AnimatedLogo";
import UserMenu from "../components/UserMenu";

// Field type configurations
const FIELD_TYPES = [
  { value: "text", label: "Texte", icon: Type },
  { value: "email", label: "Email", icon: Mail },
  { value: "phone", label: "Téléphone", icon: Phone },
  { value: "date", label: "Date", icon: Calendar },
  { value: "number", label: "Nombre", icon: Hash },
  { value: "url", label: "URL", icon: LinkIcon },
  { value: "select", label: "Liste déroulante", icon: List },
  { value: "checkbox", label: "Case à cocher", icon: ToggleLeft },
  { value: "address", label: "Adresse", icon: MapPin },
  { value: "postal_code", label: "Code postal", icon: Hash },
  { value: "country", label: "Pays", icon: Globe },
];

const getFieldIcon = (type) => {
  const field = FIELD_TYPES.find(f => f.value === type);
  return field?.icon || Type;
};

export default function ParticipantLists() {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showEditListSheet, setShowEditListSheet] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // New list form
  const [newListName, setNewListName] = useState("");
  const [newListColor, setNewListColor] = useState("slate");
  const [newListDescription, setNewListDescription] = useState("");
  
  // Custom fields for list editing
  const [editingFields, setEditingFields] = useState([]);
  
  // New participant form
  const [participantForm, setParticipantForm] = useState({});
  
  // File upload ref
  const fileInputRef = useRef(null);
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState({});
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportOnlyConsent, setExportOnlyConsent] = useState(false);

  useEffect(() => {
    loadLists();
  }, []);

  useEffect(() => {
    if (selectedList) {
      loadParticipants(selectedList.id);
    }
  }, [selectedList?.id]);

  const loadLists = async () => {
    try {
      const res = await api.get("/participant-lists");
      setLists(res.data);
      if (res.data.length > 0 && !selectedList) {
        setSelectedList(res.data[0]);
      }
    } catch (error) {
      console.error("Error loading lists:", error);
      toast.error("Erreur lors du chargement des listes");
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async (listId) => {
    try {
      const res = await api.get(`/participant-lists/${listId}/participants`);
      setParticipants(res.data);
    } catch (error) {
      console.error("Error loading participants:", error);
      setParticipants([]);
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    setSaving(true);
    try {
      // Create with default fields
      const defaultFields = [
        { name: "Nom", field_type: "text", required: true, placeholder: "Nom complet" },
        { name: "Email", field_type: "email", required: true, placeholder: "email@exemple.com" },
        { name: "Téléphone", field_type: "phone", required: false, placeholder: "06 12 34 56 78" }
      ];
      
      const res = await api.post("/participant-lists", { 
        name: newListName,
        color: newListColor,
        description: newListDescription,
        custom_fields: defaultFields
      });
      setLists([...lists, res.data]);
      setSelectedList(res.data);
      setNewListName("");
      setNewListColor("slate");
      setNewListDescription("");
      setShowCreateModal(false);
      toast.success("Liste créée avec succès");
    } catch (error) {
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditList = () => {
    if (!selectedList) return;
    setEditingFields(selectedList.custom_fields || []);
    setShowEditListSheet(true);
  };

  const handleAddField = () => {
    const newField = {
      id: `field-${Date.now()}`,
      name: "",
      field_type: "text",
      required: false,
      options: [],
      placeholder: ""
    };
    setEditingFields([...editingFields, newField]);
  };

  const handleUpdateField = (index, updates) => {
    const updated = [...editingFields];
    updated[index] = { ...updated[index], ...updates };
    setEditingFields(updated);
  };

  const handleRemoveField = (index) => {
    // Don't remove core fields
    const field = editingFields[index];
    if (["Nom", "Email"].includes(field.name) && field.required) {
      toast.error("Ce champ ne peut pas être supprimé");
      return;
    }
    setEditingFields(editingFields.filter((_, i) => i !== index));
  };

  const handleSaveListFields = async () => {
    if (!selectedList) return;
    
    // Validate fields
    for (const field of editingFields) {
      if (!field.name.trim()) {
        toast.error("Tous les champs doivent avoir un nom");
        return;
      }
    }
    
    setSaving(true);
    try {
      const res = await api.put(`/participant-lists/${selectedList.id}`, {
        name: selectedList.name,
        color: selectedList.color,
        description: selectedList.description || "",
        custom_fields: editingFields
      });
      
      // Update list in state
      setLists(lists.map(l => l.id === selectedList.id ? res.data : l));
      setSelectedList(res.data);
      setShowEditListSheet(false);
      toast.success("Structure de la liste mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddParticipant = () => {
    // Initialize form with custom fields
    const initialForm = {};
    (selectedList?.custom_fields || []).forEach(field => {
      initialForm[field.id || field.name] = field.field_type === "checkbox" ? false : "";
    });
    setParticipantForm(initialForm);
    setEditingParticipant(null);
    setShowAddParticipant(true);
  };

  const handleOpenEditParticipant = (participant) => {
    const form = {
      name: participant.name,
      email: participant.email,
      phone: participant.phone || "",
      ...participant.custom_data
    };
    setParticipantForm(form);
    setEditingParticipant(participant);
    setShowAddParticipant(true);
  };

  const handleSaveParticipant = async () => {
    // Validate required fields
    const fields = selectedList?.custom_fields || [];
    
    // Find name and email from form
    let name = participantForm.name || "";
    let email = participantForm.email || "";
    let phone = participantForm.phone || "";
    
    // Check for email field
    const emailField = fields.find(f => f.field_type === "email" || f.name.toLowerCase() === "email");
    if (emailField) {
      email = participantForm[emailField.id || emailField.name] || email;
    }
    
    // Check for name field
    const nameField = fields.find(f => f.name.toLowerCase() === "nom" || f.name.toLowerCase() === "name");
    if (nameField) {
      name = participantForm[nameField.id || nameField.name] || name;
    }
    
    // Check for phone field
    const phoneField = fields.find(f => f.field_type === "phone" || f.name.toLowerCase().includes("téléphone"));
    if (phoneField) {
      phone = participantForm[phoneField.id || phoneField.name] || phone;
    }
    
    if (!email) {
      toast.error("L'email est obligatoire");
      return;
    }
    
    if (!name) {
      toast.error("Le nom est obligatoire");
      return;
    }
    
    // Build custom_data from non-core fields
    const customData = {};
    fields.forEach(field => {
      if (!["Nom", "Email", "Téléphone"].includes(field.name)) {
        const fieldKey = field.id || field.name;
        customData[fieldKey] = participantForm[fieldKey] || "";
      }
    });
    
    setSaving(true);
    try {
      const data = { name, email, phone, custom_data: customData };
      
      if (editingParticipant) {
        const res = await api.put(
          `/participant-lists/${selectedList.id}/participants/${editingParticipant.id}`,
          data
        );
        setParticipants(participants.map(p => p.id === editingParticipant.id ? res.data : p));
        toast.success("Participant mis à jour");
      } else {
        const res = await api.post(`/participant-lists/${selectedList.id}/participants`, data);
        setParticipants([...participants, res.data]);
        toast.success("Participant ajouté");
      }
      
      setShowAddParticipant(false);
      setParticipantForm({});
      setEditingParticipant(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!window.confirm("Supprimer ce participant ?")) return;
    try {
      await api.delete(`/participant-lists/${selectedList.id}/participants/${participantId}`);
      setParticipants(participants.filter(p => p.id !== participantId));
      toast.success("Participant supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSendVerification = async (participant) => {
    try {
      await api.post(`/participant-lists/${selectedList.id}/participants/${participant.id}/send-verification`);
      toast.success(`Email de vérification envoyé à ${participant.email}`);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedList) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post(
        `/participant-lists/${selectedList.id}/participants/import`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" }}
      );
      
      await loadParticipants(selectedList.id);
      
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.imported} importés, ${res.data.total_errors} erreurs`);
      } else {
        toast.success(`${res.data.imported} participants importés`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenExportModal = () => {
    if (participants.length === 0) {
      toast.error("Aucun participant à exporter");
      return;
    }
    
    // Initialize columns selection
    const fields = selectedList?.custom_fields || [];
    const initialColumns = {
      name: true,
      email: true,
      phone: true,
      email_verified: false,
      rgpd_consent: true,
      created_at: false
    };
    fields.filter(f => !["Nom", "Email", "Téléphone"].includes(f.name)).forEach(field => {
      initialColumns[field.id || field.name] = true;
    });
    setExportColumns(initialColumns);
    setShowExportModal(true);
  };

  const handleExportData = () => {
    // Filter participants if needed
    let dataToExport = participants;
    if (exportOnlyConsent) {
      dataToExport = participants.filter(p => p.rgpd_consent === true);
    }
    
    if (dataToExport.length === 0) {
      toast.error("Aucun participant avec consentement marketing à exporter");
      return;
    }
    
    const fields = selectedList?.custom_fields || [];
    
    // Build headers based on selected columns
    const headers = [];
    const columnKeys = [];
    
    if (exportColumns.name) { headers.push("Nom"); columnKeys.push("name"); }
    if (exportColumns.email) { headers.push("Email"); columnKeys.push("email"); }
    if (exportColumns.phone) { headers.push("Téléphone"); columnKeys.push("phone"); }
    if (exportColumns.email_verified) { headers.push("Email vérifié"); columnKeys.push("email_verified"); }
    if (exportColumns.rgpd_consent) { headers.push("Consentement marketing"); columnKeys.push("rgpd_consent"); }
    if (exportColumns.created_at) { headers.push("Date d'inscription"); columnKeys.push("created_at"); }
    
    fields.filter(f => !["Nom", "Email", "Téléphone"].includes(f.name)).forEach(field => {
      const key = field.id || field.name;
      if (exportColumns[key]) {
        headers.push(field.name);
        columnKeys.push(key);
      }
    });
    
    // Build rows
    const rows = dataToExport.map(p => {
      return columnKeys.map(key => {
        if (key === "name") return p.name || "";
        if (key === "email") return p.email || "";
        if (key === "phone") return p.phone || "";
        if (key === "email_verified") return p.email_verified ? "Oui" : "Non";
        if (key === "rgpd_consent") return p.rgpd_consent ? "Oui" : "Non";
        if (key === "created_at") return p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR") : "";
        return p.custom_data?.[key] || "";
      });
    });
    
    if (exportFormat === "csv") {
      const csvContent = [
        headers.join(","), 
        ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      ].join("\n");
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedList.name}_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (exportFormat === "json") {
      const jsonData = dataToExport.map(p => {
        const obj = {};
        columnKeys.forEach((key, i) => {
          if (key === "name") obj.nom = p.name;
          else if (key === "email") obj.email = p.email;
          else if (key === "phone") obj.telephone = p.phone;
          else if (key === "email_verified") obj.email_verifie = p.email_verified;
          else if (key === "rgpd_consent") obj.consentement_marketing = p.rgpd_consent;
          else if (key === "created_at") obj.date_inscription = p.created_at;
          else obj[headers[i]] = p.custom_data?.[key] || "";
        });
        return obj;
      });
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedList.name}_export_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
    
    setShowExportModal(false);
    toast.success(`${dataToExport.length} participants exportés`);
  };

  const handleExportCSV = () => {
    if (participants.length === 0) {
      toast.error("Aucun participant à exporter");
      return;
    }
    
    const fields = selectedList?.custom_fields || [];
    const headers = ["Nom", "Email", "Téléphone", "Email vérifié", ...fields.filter(f => !["Nom", "Email", "Téléphone"].includes(f.name)).map(f => f.name)];
    
    const rows = participants.map(p => {
      const row = [
        p.name,
        p.email,
        p.phone || "",
        p.email_verified ? "Oui" : "Non"
      ];
      fields.filter(f => !["Nom", "Email", "Téléphone"].includes(f.name)).forEach(field => {
        row.push(p.custom_data?.[field.id || field.name] || "");
      });
      return row;
    });
    
    const csvContent = [headers.join(","), ...rows.map(r => r.map(v => `"${v}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedList.name}_participants.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteList = async () => {
    if (!selectedList) return;
    if (!window.confirm(`Supprimer la liste "${selectedList.name}" et tous ses participants ?`)) return;
    
    try {
      await api.delete(`/participant-lists/${selectedList.id}`);
      const newLists = lists.filter(l => l.id !== selectedList.id);
      setLists(newLists);
      setSelectedList(newLists[0] || null);
      setParticipants([]);
      toast.success("Liste supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredParticipants = participants.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone?.includes(searchTerm)
  );

  const getListColor = (color) => {
    const colors = {
      blue: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
      amber: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
      purple: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
      green: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      red: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
      slate: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
    };
    return colors[color] || colors.slate;
  };

  // Render field input based on type
  const renderFieldInput = (field, value, onChange) => {
    const fieldKey = field.id || field.name;
    
    switch (field.field_type) {
      case "email":
        return (
          <Input
            type="email"
            placeholder={field.placeholder || "email@exemple.com"}
            value={value || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className="bg-background"
            required={field.required}
          />
        );
      case "phone":
        return (
          <Input
            type="tel"
            placeholder={field.placeholder || "06 12 34 56 78"}
            value={value || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className="bg-background"
            required={field.required}
          />
        );
      case "date":
        return (
          <Input
            type="date"
            value={value || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className="bg-background"
            required={field.required}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            placeholder={field.placeholder || "0"}
            value={value || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className="bg-background"
            required={field.required}
          />
        );
      case "url":
        return (
          <Input
            type="url"
            placeholder={field.placeholder || "https://..."}
            value={value || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className="bg-background"
            required={field.required}
          />
        );
      case "select":
        return (
          <Select value={value || ""} onValueChange={(v) => onChange(fieldKey, v)}>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt, i) => (
                <SelectItem key={i} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "checkbox":
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={value || false}
              onCheckedChange={(checked) => onChange(fieldKey, checked)}
            />
            <span className="text-sm text-muted-foreground">{field.placeholder || "Oui"}</span>
          </div>
        );
      default:
        return (
          <Input
            type="text"
            placeholder={field.placeholder || ""}
            value={value || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className="bg-background"
            required={field.required}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <AnimatedLogo linkTo="/dashboard" showAnimation={true} size="small" />
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Title */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>
              Mes listes de participants
            </h1>
            <p className="text-muted-foreground">
              Créez et gérez vos bases de données de participants
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="rounded-full"
            data-testid="create-list-btn"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            Nouvelle liste
          </Button>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar - Lists */}
          <div className="lg:col-span-1">
            <Card className="bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Catégories</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {lists.map((list) => (
                  <button
                    key={list.id}
                    onClick={() => setSelectedList(list)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      selectedList?.id === list.id 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted"
                    }`}
                    data-testid={`list-${list.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4" />
                      <span className="font-medium truncate">{list.name}</span>
                    </div>
                    <Badge className={getListColor(list.color)}>
                      {list.count || 0}
                    </Badge>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Participants */}
          <div className="lg:col-span-3">
            <Card className="bg-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Users className="w-5 h-5" />
                      {selectedList?.name || "Sélectionnez une liste"}
                    </CardTitle>
                    <CardDescription>
                      {filteredParticipants.length} participant{filteredParticipants.length !== 1 ? 's' : ''}
                      {selectedList?.custom_fields?.length > 3 && (
                        <span className="ml-2 text-xs">
                          ({selectedList.custom_fields.length} champs)
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full"
                      onClick={handleOpenAddParticipant}
                      disabled={!selectedList}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                    
                    {selectedList && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="rounded-full">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleOpenEditList}>
                            <Settings className="w-4 h-4 mr-2" />
                            Configurer les champs
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                            <Upload className="w-4 h-4 mr-2" />
                            Importer CSV/Excel
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleOpenExportModal}>
                            <FileDown className="w-4 h-4 mr-2" />
                            Exporter (avancé)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleExportCSV}>
                            <Download className="w-4 h-4 mr-2" />
                            Export CSV rapide
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={handleDeleteList}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer la liste
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                
                {/* Hidden file input for import */}
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleImportFile}
                />
                
                {/* Search */}
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Rechercher un participant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-background"
                  />
                </div>
              </CardHeader>
              
              <CardContent>
                {filteredParticipants.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">
                      {searchTerm ? "Aucun résultat" : "Cette liste est vide"}
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={handleOpenAddParticipant}
                      className="rounded-full"
                      disabled={!selectedList}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Ajouter un participant
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredParticipants.map((participant) => (
                      <div 
                        key={participant.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-sm font-medium text-primary">
                              {participant.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground truncate">{participant.name}</p>
                              {participant.email_verified ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3" />
                                {participant.email}
                              </span>
                              {participant.phone && (
                                <span className="flex items-center gap-1 hidden sm:flex">
                                  <Phone className="w-3 h-3" />
                                  {participant.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!participant.email_verified && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-primary"
                              onClick={() => handleSendVerification(participant)}
                              title="Envoyer email de vérification"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => handleOpenEditParticipant(participant)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteParticipant(participant.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Create List Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Nouvelle liste</DialogTitle>
            <DialogDescription>
              Créez une nouvelle catégorie pour organiser vos participants
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de la liste *</Label>
              <Input 
                placeholder="Ex: Clients VIP, Prospects..."
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <Select value={newListColor} onValueChange={setNewListColor}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blue">Bleu</SelectItem>
                  <SelectItem value="amber">Orange</SelectItem>
                  <SelectItem value="purple">Violet</SelectItem>
                  <SelectItem value="green">Vert</SelectItem>
                  <SelectItem value="red">Rouge</SelectItem>
                  <SelectItem value="slate">Gris</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Input 
                placeholder="Description de cette liste..."
                value={newListDescription}
                onChange={(e) => setNewListDescription(e.target.value)}
                className="bg-background"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateList} disabled={saving || !newListName.trim()}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit List Fields Sheet */}
      <Sheet open={showEditListSheet} onOpenChange={setShowEditListSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle style={{ fontFamily: 'Outfit' }}>
              Configurer les champs
            </SheetTitle>
            <SheetDescription>
              Ajoutez ou modifiez les champs de votre liste "{selectedList?.name}"
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            {editingFields.map((field, index) => {
              const FieldIcon = getFieldIcon(field.field_type);
              const isCore = ["Nom", "Email"].includes(field.name) && field.required;
              
              return (
                <div 
                  key={field.id || index}
                  className={`p-4 rounded-lg border ${isCore ? "bg-muted/50 border-dashed" : "bg-background"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-2">
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Nom du champ"
                          value={field.name}
                          onChange={(e) => handleUpdateField(index, { name: e.target.value })}
                          className="flex-1"
                          disabled={isCore}
                        />
                        {!isCore && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveField(index)}
                            className="text-muted-foreground hover:text-destructive shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <Select 
                          value={field.field_type} 
                          onValueChange={(v) => handleUpdateField(index, { field_type: v })}
                          disabled={isCore}
                        >
                          <SelectTrigger className="bg-background">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <div className="flex items-center gap-2">
                                  <type.icon className="w-4 h-4" />
                                  {type.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={field.required}
                            onCheckedChange={(checked) => handleUpdateField(index, { required: checked })}
                            disabled={isCore}
                          />
                          <span className="text-sm">Obligatoire</span>
                        </div>
                      </div>
                      
                      {field.field_type === "select" && (
                        <div className="space-y-2">
                          <Label className="text-xs">Options (une par ligne)</Label>
                          <textarea
                            className="w-full p-2 text-sm border rounded-md bg-background resize-none"
                            rows={3}
                            placeholder="Option 1&#10;Option 2&#10;Option 3"
                            value={(field.options || []).join("\n")}
                            onChange={(e) => handleUpdateField(index, { 
                              options: e.target.value.split("\n").filter(o => o.trim())
                            })}
                          />
                        </div>
                      )}
                      
                      <Input
                        placeholder="Placeholder (optionnel)"
                        value={field.placeholder || ""}
                        onChange={(e) => handleUpdateField(index, { placeholder: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={handleAddField}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un champ
            </Button>
          </div>
          
          <div className="mt-6 flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowEditListSheet(false)}
            >
              Annuler
            </Button>
            <Button 
              className="flex-1"
              onClick={handleSaveListFields}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Add/Edit Participant Modal */}
      <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              {editingParticipant ? "Modifier le participant" : "Ajouter un participant"}
            </DialogTitle>
            <DialogDescription>
              {editingParticipant 
                ? "Modifiez les informations du participant" 
                : `Ajoutez un participant à la liste "${selectedList?.name}"`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {(selectedList?.custom_fields || []).map((field) => {
              const fieldKey = field.id || field.name;
              const value = participantForm[fieldKey];
              
              return (
                <div key={fieldKey} className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {field.name}
                    {field.required && <span className="text-destructive">*</span>}
                    {field.field_type === "email" && (
                      <span className="text-xs text-muted-foreground ml-1">(obligatoire)</span>
                    )}
                  </Label>
                  {renderFieldInput(
                    field, 
                    value, 
                    (key, val) => setParticipantForm({ ...participantForm, [key]: val })
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParticipant(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveParticipant} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingParticipant ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }} className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-primary" />
              Exporter les participants
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les colonnes à inclure dans l'export pour vos campagnes marketing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Format selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Format d'export</Label>
              <div className="flex gap-2">
                <Button 
                  variant={exportFormat === "csv" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("csv")}
                  className="flex-1"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV (Excel)
                </Button>
                <Button 
                  variant={exportFormat === "json" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("json")}
                  className="flex-1"
                >
                  JSON (API)
                </Button>
              </div>
            </div>
            
            {/* RGPD Filter */}
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="consent-filter"
                      checked={exportOnlyConsent}
                      onCheckedChange={setExportOnlyConsent}
                    />
                    <Label htmlFor="consent-filter" className="font-medium text-green-800 dark:text-green-200 cursor-pointer">
                      Exporter uniquement les contacts avec consentement marketing
                    </Label>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Recommandé pour les campagnes email/SMS (conformité RGPD)
                  </p>
                </div>
              </div>
            </div>
            
            {/* Columns selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Colonnes à exporter</Label>
              
              {/* Core columns */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                  <Checkbox
                    checked={exportColumns.name}
                    onCheckedChange={(v) => setExportColumns({...exportColumns, name: v})}
                    id="col-name"
                  />
                  <Label htmlFor="col-name" className="text-sm cursor-pointer">Nom</Label>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                  <Checkbox
                    checked={exportColumns.email}
                    onCheckedChange={(v) => setExportColumns({...exportColumns, email: v})}
                    id="col-email"
                  />
                  <Label htmlFor="col-email" className="text-sm cursor-pointer">Email</Label>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                  <Checkbox
                    checked={exportColumns.phone}
                    onCheckedChange={(v) => setExportColumns({...exportColumns, phone: v})}
                    id="col-phone"
                  />
                  <Label htmlFor="col-phone" className="text-sm cursor-pointer">Téléphone</Label>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                  <Checkbox
                    checked={exportColumns.email_verified}
                    onCheckedChange={(v) => setExportColumns({...exportColumns, email_verified: v})}
                    id="col-verified"
                  />
                  <Label htmlFor="col-verified" className="text-sm cursor-pointer">Email vérifié</Label>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                  <Checkbox
                    checked={exportColumns.rgpd_consent}
                    onCheckedChange={(v) => setExportColumns({...exportColumns, rgpd_consent: v})}
                    id="col-consent"
                  />
                  <Label htmlFor="col-consent" className="text-sm cursor-pointer">Consentement</Label>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                  <Checkbox
                    checked={exportColumns.created_at}
                    onCheckedChange={(v) => setExportColumns({...exportColumns, created_at: v})}
                    id="col-date"
                  />
                  <Label htmlFor="col-date" className="text-sm cursor-pointer">Date inscription</Label>
                </div>
              </div>
              
              {/* Custom fields */}
              {(selectedList?.custom_fields || []).filter(f => !["Nom", "Email", "Téléphone"].includes(f.name)).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-2">Champs personnalisés</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(selectedList?.custom_fields || [])
                      .filter(f => !["Nom", "Email", "Téléphone"].includes(f.name))
                      .map((field) => {
                        const key = field.id || field.name;
                        return (
                          <div key={key} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                            <Checkbox
                              checked={exportColumns[key] || false}
                              onCheckedChange={(v) => setExportColumns({...exportColumns, [key]: v})}
                              id={`col-${key}`}
                            />
                            <Label htmlFor={`col-${key}`} className="text-sm cursor-pointer truncate">{field.name}</Label>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            
            {/* Summary */}
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">{exportOnlyConsent ? participants.filter(p => p.rgpd_consent).length : participants.length}</span> participants seront exportés
                {exportOnlyConsent && <span className="text-green-600"> (avec consentement)</span>}
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleExportData} className="gap-2">
              <Download className="w-4 h-4" />
              Exporter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
